import { UATFormData } from './types';

/** Bump when checklist structure changes so stale localStorage drafts are not restored. */
export const UAT_DRAFT_STORAGE_KEY = 'uatResponse_2026_08';

export const initialChecklistData: UATFormData = {
  categories: [
    {
      id: '1',
      title: 'Testing Environment Details',
      description: 'Device and browser used for this UAT session (excluded from pass/fail progress)',
      user_type: 'partner',
      items: [
        {
          id: '1.1',
          title: 'Device Type',
          description: 'Select the type of device being used for testing',
          status: 'pending',
          priority: 'P0',
          category: '1',
        },
        {
          id: '1.2',
          title: 'Web Browser',
          description: 'Select the web browser being used for testing',
          status: 'pending',
          priority: 'P0',
          category: '1',
        },
        {
          id: '1.3',
          title: 'Operating System',
          description: 'Select the operating system being used for testing',
          status: 'pending',
          priority: 'P0',
          category: '1',
        },
      ],
    },
    {
      id: '2',
      title: 'Partner Onboarding & Community Setup',
      description: 'Kitchen operator signs up, verifies, and publishes a community that licensees can find and join',
      user_type: 'partner',
      items: [
        {
          id: '2.1',
          title: 'Partner registration & email verification',
          description: 'Create a partner account, complete OTP/email verification, and land in the community dashboard',
          status: 'pending',
          priority: 'P0',
          category: '2',
        },
        {
          id: '2.2',
          title: 'Community profile',
          description: 'Set community name, location, hours, description, and media so the kitchen is discoverable and credible',
          status: 'pending',
          priority: 'P0',
          category: '2',
        },
        {
          id: '2.3',
          title: 'Team roles & invites',
          description: 'Invite staff with viewer/editor/admin roles and confirm permissions for bookings, payments, and settings',
          status: 'pending',
          priority: 'P1',
          category: '2',
        },
        {
          id: '2.4',
          title: 'Notifications',
          description: 'Configure email (and in-app) notifications for requests, bookings, and member activity',
          status: 'pending',
          priority: 'P2',
          category: '2',
        },
      ],
    },
    {
      id: '3',
      title: 'Membership Plans, Agreements & Requirements',
      description: 'Replace spreadsheet leases with membership, resource, and dual plans plus agreements and credential gates',
      user_type: 'partner',
      items: [
        {
          id: '3.1',
          title: 'Membership & dual tiers',
          description: 'Create membership and dual plans with rates, recurrence, perks, 24/7 access, linked resources, and inventory_limits (included qty)',
          status: 'pending',
          priority: 'P0',
          category: '3',
        },
        {
          id: '3.2',
          title: 'Resource booking plans',
          description: 'Create kitchen/resource booking plans (hourly/package) with peak/off-peak rates, associated resources, and optional bundled inventory',
          status: 'pending',
          priority: 'P0',
          category: '3',
        },
        {
          id: '3.3',
          title: 'Member agreement',
          description: 'Publish and preview the food-business / kitchen-use agreement with placeholders filled from member and community data',
          status: 'pending',
          priority: 'P0',
          category: '3',
        },
        {
          id: '3.4',
          title: 'Onboarding requirements & credentials',
          description: 'Define required docs (ServSafe, COI, permits) and confirm they block or flag incomplete applications until reviewed',
          status: 'pending',
          priority: 'P0',
          category: '3',
        },
        {
          id: '3.5',
          title: 'Self-service registration',
          description: 'Configure and complete the public / event self-service flow so applicants can register without a staff invite',
          status: 'pending',
          priority: 'P1',
          category: '3',
        },
      ],
    },
    {
      id: '4',
      title: 'Kitchen Resources & Inventory',
      description: 'Stations, hours, fees, and food-specific inventory (reusable equipment vs consumables) that drive utilization and extra revenue',
      user_type: 'partner',
      items: [
        {
          id: '4.1',
          title: 'Kitchen resource setup',
          description: 'Create/edit a kitchen resource with operating hours, capacity, floor plan/media, fees, and discounts',
          status: 'pending',
          priority: 'P0',
          category: '4',
        },
        {
          id: '4.2',
          title: 'Additional resource types',
          description: 'Optionally list training, documentation, expert, funding, or farm resources that support licensee growth (exclude if not offered)',
          status: 'pending',
          priority: 'P3',
          category: '4',
        },
        {
          id: '4.3',
          title: 'Inventory catalog (add/edit products)',
          description: 'Add consumable and reusable items with pricing, images, units, and Track Usage / usage-cost fields where relevant',
          status: 'pending',
          priority: 'P0',
          category: '4',
        },
        {
          id: '4.4',
          title: 'Reusable equipment occupancy',
          description: 'Confirm reusable stock uses overlapping approved/in-progress bookings (available / in use / upcoming / out of stock), not stored available_quantity',
          status: 'pending',
          priority: 'P0',
          category: '4',
        },
        {
          id: '4.5',
          title: 'Inventory item details',
          description: 'Open an item: overview occupancy, booking sessions (click a row to manage the request), admin log, and audit trail',
          status: 'pending',
          priority: 'P1',
          category: '4',
        },
      ],
    },
    {
      id: '5',
      title: 'Payments, Stripe & Integrations',
      description: 'Collect licensee payments and connect the tools kitchens already use (Stripe, calendar, books, CRM)',
      user_type: 'partner',
      items: [
        {
          id: '5.1',
          title: 'Stripe Connect onboarding',
          description: 'Complete Stripe Connect account setup and open the embedded payments dashboard from Settings or Payments',
          status: 'pending',
          priority: 'P0',
          category: '5',
        },
        {
          id: '5.2',
          title: 'Payouts & transaction history',
          description: 'View payouts and community transactions; confirm amounts match approved bookings and inventory charges',
          status: 'pending',
          priority: 'P0',
          category: '5',
        },
        {
          id: '5.3',
          title: 'Google Calendar sync',
          description: 'Connect Google Calendar so approved bookings appear on the kitchen calendar (exclude if not in this test)',
          status: 'pending',
          priority: 'P1',
          category: '5',
        },
        {
          id: '5.4',
          title: 'QuickBooks / HubSpot / Mailchimp / Drive',
          description: 'Connect at least one ops integration used instead of spreadsheets + email (exclude unused integrations)',
          status: 'pending',
          priority: 'P2',
          category: '5',
        },
      ],
    },
    {
      id: '6',
      title: 'Member Discovery, Application & Profile',
      description: 'Licensee journey: find a kitchen, apply, upload credentials, and manage membership from the member app',
      user_type: 'member',
      items: [
        {
          id: '6.1',
          title: 'Search & discover kitchens',
          description: 'Search/filter kitchens and open a listing with hours, plans, and inventory that match the partner community',
          status: 'pending',
          priority: 'P0',
          category: '6',
        },
        {
          id: '6.2',
          title: 'Membership application',
          description: 'Submit a membership request with required credentials and agree to the kitchen-use agreement',
          status: 'pending',
          priority: 'P0',
          category: '6',
        },
        {
          id: '6.3',
          title: 'Plan selection & subscription',
          description: 'Choose a membership, resource, or dual plan and complete checkout / subscription where payment is required',
          status: 'pending',
          priority: 'P0',
          category: '6',
        },
        {
          id: '6.4',
          title: 'Member profile & balances',
          description: 'View membership status, remaining hours/balances, and uploaded credentials from the member account',
          status: 'pending',
          priority: 'P1',
          category: '6',
        },
      ],
    },
    {
      id: '7',
      title: 'Partner Vetting & Member Records',
      description: 'Approve the right licensees and keep a single record of plans, credentials, and balances (audit-ready ops)',
      user_type: 'partner',
      items: [
        {
          id: '7.1',
          title: 'Review membership requests',
          description: 'Approve, decline, or request changes on applications; confirm members appear on the Community members list',
          status: 'pending',
          priority: 'P0',
          category: '7',
        },
        {
          id: '7.2',
          title: 'Credential review',
          description: 'Preview PDFs/images, approve or reject credentials, and confirm incomplete docs are visible on the member record',
          status: 'pending',
          priority: 'P0',
          category: '7',
        },
        {
          id: '7.3',
          title: 'Edit members & plans',
          description: 'Add/edit members in bulk or individually; assign plans, inventory limits, and balances without leaving the dashboard',
          status: 'pending',
          priority: 'P1',
          category: '7',
        },
        {
          id: '7.4',
          title: 'Member search, filters & balance',
          description: 'Search/filter members and sort by member balance; confirm hours remaining match bookings and plan limits',
          status: 'pending',
          priority: 'P1',
          category: '7',
        },
      ],
    },
    {
      id: '8',
      title: 'Member Booking Journey',
      description: 'Licensee books a station, adds inventory for the session, and pays - the core replacement for shared calendars and clipboards',
      user_type: 'member',
      items: [
        {
          id: '8.1',
          title: 'Availability calendar',
          description: 'View kitchen availability (including 24/7 tiers where enabled) and select a session that does not overlap existing bookings',
          status: 'pending',
          priority: 'P0',
          category: '8',
        },
        {
          id: '8.2',
          title: 'Book with inventory',
          description: 'Add reusable and consumable order items for the selected window; remaining reusable qty reflects that session, not a global count',
          status: 'pending',
          priority: 'P0',
          category: '8',
        },
        {
          id: '8.3',
          title: 'Included inventory vs billed extras',
          description: 'On dual/membership plans, included inventory_limits qty is not charged; extras beyond the allowance are priced and shown at checkout',
          status: 'pending',
          priority: 'P0',
          category: '8',
        },
        {
          id: '8.4',
          title: 'Recurring bookings',
          description: 'Create or manage a recurring booking within plan max hours; confirm later occurrences stay on the calendar',
          status: 'pending',
          priority: 'P1',
          category: '8',
        },
        {
          id: '8.5',
          title: 'Invite booking teammates',
          description: 'Add team members on a booking where the kitchen allows shared sessions (exclude if not used)',
          status: 'pending',
          priority: 'P2',
          category: '8',
        },
        {
          id: '8.6',
          title: 'Member payment for booking',
          description: 'Complete card/ACH checkout for the booking and see the request move to pending/approved as expected',
          status: 'pending',
          priority: 'P0',
          category: '8',
        },
      ],
    },
    {
      id: '10',
      title: 'Partner Request Handling & Billing Accuracy',
      description: 'Approve, edit, and bill bookings so utilization and inventory charges are correct before they hit Stripe',
      user_type: 'partner',
      items: [
        {
          id: '10.1',
          title: 'Resource request inbox',
          description: 'Open pending/approved/in-progress/completed requests from Resources or Payments and manage a request in Request Details',
          status: 'pending',
          priority: 'P0',
          category: '10',
        },
        {
          id: '10.2',
          title: 'Approve, decline, or update a booking',
          description: 'Change dates/times/status; confirm calendar, member view, and (if connected) Google Calendar stay in sync',
          status: 'pending',
          priority: 'P0',
          category: '10',
        },
        {
          id: '10.3',
          title: 'Order items & dual-plan inventory_limits',
          description: 'Included qty shows $0.00 with strikethrough list price; partial included/charged lines and totals match billed cost (not full list)',
          status: 'pending',
          priority: 'P0',
          category: '10',
        },
        {
          id: '10.4',
          title: 'Reusable stock after approve/complete',
          description: 'Approving/in-progress reserves reusable items; completed/canceled/pending do not keep holding stock for occupancy math',
          status: 'pending',
          priority: 'P1',
          category: '10',
        },
      ],
    },
    {
      id: '11',
      title: 'Communication',
      description: 'In-platform messaging so operators and licensees are not stuck on WhatsApp/email for booking issues',
      user_type: 'partner',
      items: [
        {
          id: '11.1',
          title: 'Partner ↔ member messaging',
          description: 'Start or reply to a conversation from Messages; confirm the other party can read it in their app',
          status: 'pending',
          priority: 'P1',
          category: '11',
        },
        {
          id: '11.2',
          title: 'Message notifications',
          description: 'Receive a notification or unread indicator for a new message (exclude if notifications are off for this test)',
          status: 'pending',
          priority: 'P2',
          category: '11',
        },
      ],
    },
    {
      id: '12',
      title: 'Dashboard Analytics & Reporting',
      description: 'Utilization and revenue overview for operators replacing spreadsheet reporting for funders and landlords',
      user_type: 'partner',
      items: [
        {
          id: '12.1',
          title: 'Dashboard overview',
          description: 'Confirm Dashboard shows current community activity (members, bookings, or revenue widgets) without a blank or stale state',
          status: 'pending',
          priority: 'P1',
          category: '12',
        },
        {
          id: '12.2',
          title: 'Booking & payment reporting',
          description: 'Use Payments/transactions filters to answer who booked, what they paid, and whether inventory add-ons were billed',
          status: 'pending',
          priority: 'P1',
          category: '12',
        },
      ],
    },
    {
      id: '9',
      title: 'Overall Assessment',
      description: 'Would this kitchen replace spreadsheets + shared calendar + QuickBooks for day-to-day licensee ops?',
      user_type: 'partner',
      items: [
        {
          id: '9.1',
          title: 'Functionality rating',
          description: 'Rate whether booking, membership, inventory, and payments cover the kitchen’s real workflow',
          status: 'pending',
          priority: 'P0',
          category: '9',
        },
        {
          id: '9.2',
          title: 'User experience rating',
          description: 'Rate whether staff and licensees can complete the journey without tribal knowledge or workarounds',
          status: 'pending',
          priority: 'P0',
          category: '9',
        },
        {
          id: '9.3',
          title: 'Recommendation',
          description: 'Recommend go / no-go for this community’s production use, and note the top gap versus Food Corridor / spreadsheets',
          status: 'pending',
          priority: 'P0',
          category: '9',
        },
      ],
    },
  ],
  test_environment: '',
  test_period: {
    start_date: '',
    end_date: '',
  },
  testers: [],
  sign_off_status: {
    qa_lead: '',
    product_owner: '',
    technical_lead: '',
    dates: {
      qa_lead: '',
      product_owner: '',
      technical_lead: '',
    },
  },
};
